import React from 'react';
import { Box, Typography, Container, Grid, Chip } from '@mui/material';
import PageBanner from '../components/PageBanner';
import SEO from '../components/SEO';
import SectionSidebar from '../components/SectionSidebar';
import { Link as RouterLink } from 'react-router-dom';
import ExploreIcon from '@mui/icons-material/Explore';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import TempleHinduIcon from '@mui/icons-material/TempleHindu';
import LandscapeIcon from '@mui/icons-material/Landscape';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MapIcon from '@mui/icons-material/Map';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import NaturePeopleIcon from '@mui/icons-material/NaturePeople';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import RouteIcon from '@mui/icons-material/Route';
import LocalTaxiIcon from '@mui/icons-material/LocalTaxi';
import HotelIcon from '@mui/icons-material/Hotel';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import UmbrellaIcon from '@mui/icons-material/Umbrella';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import SecurityIcon from '@mui/icons-material/Security';
import PhoneIcon from '@mui/icons-material/Phone';
import PlaceIcon from '@mui/icons-material/Place';

const GOLD = '#d4af37';

const categories = [
  {
    title: 'Beaches & Coast',
    desc: 'Family-friendly beaches and safe bathing spots with nearby facilities and timings.',
    to: '/nearby/beaches',
    icon: BeachAccessIcon,
    gradient: ['#0ea5e9', '#2563eb'],
    emoji: '🏖️',
  },
  {
    title: 'Boating & Backwaters',
    desc: 'Guided boat services, timings, and contacts for scenic backwater experiences.',
    to: '/nearby/boating',
    icon: DirectionsBoatIcon,
    gradient: ['#06b6d4', '#0891b2'],
    emoji: '🚣',
  },
  {
    title: 'Viewpoints',
    desc: 'Short treks and hilltop vistas with breathtaking panoramic views.',
    to: '/nearby/viewpoints',
    icon: LandscapeIcon,
    gradient: ['#22c55e', '#16a34a'],
    emoji: '🏔️',
  },
  {
    title: 'Heritage Sites',
    desc: 'Centuries-old forts, museums, and historic structures that tell the region\'s story.',
    to: '/nearby/heritage',
    icon: AccountBalanceIcon,
    gradient: ['#f59e0b', '#d97706'],
    emoji: '🏛️',
  },
  {
    title: 'Temples Nearby',
    desc: 'Practical directory of nearby shrines with distance, timings, and deities.',
    to: '/nearby/temples',
    icon: TempleHinduIcon,
    gradient: ['#ef4444', '#dc2626'],
    emoji: '🛕',
  },
  {
    title: 'Activities & Services',
    desc: 'Guided heritage walks, transport, homestays, and local guides.',
    to: '/nearby/activities',
    icon: LocalActivityIcon,
    gradient: ['#8b5cf6', '#7c3aed'],
    emoji: '🎯',
  },
];

const itineraries = [
  {
    label: 'Half-day Pilgrim Trail',
    steps: ['Morning darshan', 'Heritage walk', 'Lunch at annadanam center'],
    accent: '#22c55e',
    gradientBg: ['#22c55e', '#16a34a'],
    tag: 'HALF DAY',
    icon: RouteIcon,
    duration: '4-5 hours',
  },
  {
    label: 'Full-day Cultural Experience',
    steps: ['Darshan & ritual', 'Boat ride', 'Nearby temples', 'Sunset viewpoint', 'Dinner'],
    accent: '#3b82f6',
    gradientBg: ['#3b82f6', '#2563eb'],
    tag: 'FULL DAY',
    icon: MapIcon,
    duration: '8-10 hours',
  },
];

const gettingAroundTips = [
  { text: 'Taxis & auto-rickshaws from the temple gate', icon: LocalTaxiIcon },
  { text: 'Pre-book vehicles on festival days', icon: DirectionsCarIcon },
  { text: 'Homestays & lodges in the vicinity', icon: HotelIcon },
  { text: 'Temple office assists with local transport', icon: SupportAgentIcon },
];

const travelTips = [
  { text: 'Best season: October to March (pleasant weather)', icon: CalendarMonthIcon },
  { text: 'Carry water and sun protection for outdoor visits', icon: UmbrellaIcon },
  { text: 'Follow lifeguard advice at beaches', icon: SecurityIcon },
  { text: 'Stay hydrated, carry water bottles', icon: WaterDropIcon },
];

export default function Nearby() {
  return (
    <Box sx={{ bgcolor: '#f8fafb', minHeight: '100vh' }}>
      <SEO
        title="Explore Nearby — Kottayil Temple | സമീപ സ്ഥലങ്ങൾ"
        description="Discover nearby attractions around Kottayil Temple (Kotta-Kovilakam Kshethram) — Akalappuzha boating, beaches, temples, viewpoints, heritage sites in Kozhikode, Kerala."
        path="/nearby"
        keywords="nearby Kottayil temple, Kozhikode attractions, nearby beaches, Akalappuzha boating, places near Muchukunnu, Kerala tourism, സമീപ സ്ഥലങ്ങൾ, കോഴിക്കോട് ടൂറിസം"
      />
      <PageBanner title="Explore Nearby" />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* ═══════════ HERO SECTION ═══════════ */}
            <Box sx={{
              borderRadius: '28px', overflow: 'hidden', mb: 4,
              boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #1a3a2a 0%, #2d5016 40%, #4a7c28 100%)',
              position: 'relative',
            }}>
              {/* Decorative circles */}
              <Box sx={{ position: 'absolute', top: -50, right: -50, width: 220, height: 220, borderRadius: '50%', background: 'radial-gradient(circle, rgba(212,175,55,0.12) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', bottom: -30, left: -30, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', top: 20, left: '40%', width: 80, height: 80, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%)' }} />
              {/* Dot pattern */}
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

              <Box sx={{ px: { xs: 3, sm: 4 }, py: { xs: 4, sm: 5 }, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2.5 }}>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '20px',
                    background: `linear-gradient(135deg, ${GOLD}, #b8941f)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: `0 12px 32px rgba(212,175,55,0.35)`,
                    border: '2px solid rgba(255,255,255,0.15)',
                  }}>
                    <ExploreIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{
                      fontWeight: 900, fontSize: { xs: '1.6rem', sm: '2rem' },
                      color: '#fff', lineHeight: 1.15, mb: 0.5,
                      textShadow: '0 2px 12px rgba(0,0,0,0.2)',
                    }}>
                      Explore Nearby
                    </Typography>
                    <Typography sx={{
                      fontSize: { xs: '0.82rem', sm: '0.9rem' },
                      color: 'rgba(255,255,255,0.6)', fontWeight: 500,
                    }}>
                      Temples, beaches, backwaters & cultural experiences
                    </Typography>
                  </Box>
                </Box>

                <Typography sx={{
                  fontSize: { xs: '0.88rem', sm: '0.95rem' },
                  color: 'rgba(255,255,255,0.55)', lineHeight: 1.9, maxWidth: 560, mb: 2.5,
                }}>
                  Muchukunnu and its surroundings offer sacred temples, serene backwaters, scenic viewpoints, heritage trails, and coastal beaches. Plan your complete pilgrimage experience.
                </Typography>

                {/* Quick stats */}
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<PlaceIcon sx={{ fontSize: '16px !important', color: `${GOLD} !important` }} />}
                    label="6 Categories"
                    sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.85)', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)', '& .MuiChip-icon': { ml: '8px' } }}
                  />
                  <Chip
                    icon={<NaturePeopleIcon sx={{ fontSize: '16px !important', color: `${GOLD} !important` }} />}
                    label="North Kerala"
                    sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.85)', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)', '& .MuiChip-icon': { ml: '8px' } }}
                  />
                  <Chip
                    icon={<PhotoCameraIcon sx={{ fontSize: '16px !important', color: `${GOLD} !important` }} />}
                    label="Must-visit Spots"
                    sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.85)', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)', '& .MuiChip-icon': { ml: '8px' } }}
                  />
                </Box>
              </Box>
            </Box>

            {/* ═══════════ CATEGORY CARDS ═══════════ */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, px: 0.5 }}>
                <Box sx={{ width: 4, height: 24, borderRadius: '4px', background: `linear-gradient(180deg, ${GOLD}, #b8941f)` }} />
                <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.92rem', md: '1.1rem' }, color: '#1a1a1a' }}>
                  What to Explore
                </Typography>
              </Box>
              <Grid container spacing={2.5}>
                {categories.map((c) => {
                  const Icon = c.icon;
                  const [gradStart, gradEnd] = c.gradient;
                  return (
                    <Grid item xs={12} sm={6} key={c.title}>
                      <Box
                        component={RouterLink}
                        to={c.to}
                        sx={{
                          display: 'block', textDecoration: 'none',
                          bgcolor: '#fff', borderRadius: '24px',
                          border: '1px solid rgba(0,0,0,0.06)',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                          overflow: 'hidden',
                          transition: 'all 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                          height: '100%',
                          '&:hover': {
                            transform: 'translateY(-8px)',
                            boxShadow: `0 20px 60px ${gradStart}20, 0 0 0 1px ${gradStart}25`,
                            '& .cardGradient': { height: 6 },
                            '& .hArrow': { transform: 'translateX(6px)' },
                            '& .iconBox': { transform: 'scale(1.08) rotate(-3deg)' },
                          },
                        }}
                      >
                        {/* Top gradient line */}
                        <Box className="cardGradient" sx={{
                          height: 4, width: '100%',
                          background: `linear-gradient(90deg, ${gradStart}, ${gradEnd})`,
                          transition: 'height 400ms ease',
                        }} />

                        <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                            <Box className="iconBox" sx={{
                              width: 52, height: 52, borderRadius: '16px',
                              background: `linear-gradient(135deg, ${gradStart}, ${gradEnd})`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              boxShadow: `0 8px 24px ${gradStart}30`,
                              flexShrink: 0,
                              transition: 'transform 400ms ease',
                            }}>
                              <Icon sx={{ fontSize: 26, color: '#fff' }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{
                                fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#1a1a1a',
                                mb: 0.3, lineHeight: 1.3,
                              }}>
                                {c.title}
                              </Typography>
                              <Typography sx={{ fontSize: '0.7rem', color: '#bbb', fontWeight: 600 }}>
                                {c.emoji} Near Muchukunnu
                              </Typography>
                            </Box>
                          </Box>

                          <Typography sx={{
                            fontSize: '0.84rem', color: '#777', lineHeight: 1.75, mb: 2.5,
                          }}>
                            {c.desc}
                          </Typography>

                          <Box sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.8,
                            px: 2, py: 0.8, borderRadius: '12px',
                            bgcolor: `${gradStart}08`,
                            border: `1px solid ${gradStart}15`,
                            transition: 'all 300ms ease',
                          }}>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: gradStart }}>
                              Explore
                            </Typography>
                            <ArrowForwardIcon className="hArrow" sx={{
                              fontSize: 16, color: gradStart,
                              transition: 'transform 300ms ease',
                            }} />
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>

            {/* ═══════════ ITINERARIES ═══════════ */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, px: 0.5 }}>
                <Box sx={{ width: 4, height: 24, borderRadius: '4px', background: 'linear-gradient(180deg, #3b82f6, #2563eb)' }} />
                <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.92rem', md: '1.1rem' }, color: '#1a1a1a' }}>
                  Suggested Itineraries
                </Typography>
              </Box>
              <Grid container spacing={2.5}>
                {itineraries.map((it) => {
                  const ItIcon = it.icon;
                  const [gStart, gEnd] = it.gradientBg;
                  return (
                    <Grid item xs={12} sm={6} key={it.label}>
                      <Box sx={{
                        bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden',
                        border: '1px solid rgba(0,0,0,0.06)',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                        height: '100%',
                        transition: 'all 350ms ease',
                        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 40px rgba(0,0,0,0.08)' },
                      }}>
                        {/* Gradient header */}
                        <Box sx={{
                          background: `linear-gradient(135deg, ${gStart}, ${gEnd})`,
                          px: 2.5, py: 2, position: 'relative', overflow: 'hidden',
                        }}>
                          <Box sx={{ position: 'absolute', top: -15, right: -15, width: 60, height: 60, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 2 }}>
                            <Box sx={{
                              width: 40, height: 40, borderRadius: '12px',
                              background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              border: '1px solid rgba(255,255,255,0.2)',
                            }}>
                              <ItIcon sx={{ fontSize: 20, color: '#fff' }} />
                            </Box>
                            <Box>
                              <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#fff', lineHeight: 1.2 }}>
                                {it.label}
                              </Typography>
                              <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>
                                {it.duration}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        {/* Steps */}
                        <Box sx={{ px: 2.5, py: 2 }}>
                          <Chip
                            label={it.tag}
                            size="small"
                            sx={{
                              bgcolor: `${it.accent}10`, color: it.accent,
                              fontWeight: 800, fontSize: '0.65rem', height: 24,
                              borderRadius: '8px', border: `1px solid ${it.accent}20`,
                              mb: 2, letterSpacing: '0.05em',
                            }}
                          />
                          {it.steps.map((step, si) => (
                            <Box key={si} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.8 }}>
                              <Box sx={{
                                width: 24, height: 24, borderRadius: '8px',
                                bgcolor: `${it.accent}10`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0,
                              }}>
                                <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: it.accent }}>
                                  {si + 1}
                                </Typography>
                              </Box>
                              <Typography sx={{ fontSize: '0.84rem', color: '#555', fontWeight: 500 }}>
                                {step}
                              </Typography>
                              {si < it.steps.length - 1 && (
                                <Typography sx={{ fontSize: '0.75rem', color: '#ddd', ml: 'auto' }}>→</Typography>
                              )}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>

            {/* ═══════════ TIPS SECTION ═══════════ */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, px: 0.5 }}>
                <Box sx={{ width: 4, height: 24, borderRadius: '4px', background: 'linear-gradient(180deg, #f59e0b, #d97706)' }} />
                <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.92rem', md: '1.1rem' }, color: '#1a1a1a' }}>
                  Visitor Information
                </Typography>
              </Box>
              <Grid container spacing={2.5}>
                {/* Getting Around */}
                <Grid item xs={12} md={6}>
                  <Box sx={{
                    bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden',
                    border: '1px solid rgba(0,0,0,0.06)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.04)', height: '100%',
                    transition: 'all 350ms ease',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 40px rgba(0,0,0,0.08)' },
                  }}>
                    <Box sx={{
                      background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                      px: 2.5, py: 1.8, display: 'flex', alignItems: 'center', gap: 1.5,
                      position: 'relative', overflow: 'hidden',
                    }}>
                      <Box sx={{ position: 'absolute', top: -10, right: -10, width: 50, height: 50, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                      <Box sx={{
                        width: 36, height: 36, borderRadius: '10px',
                        background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <DirectionsCarIcon sx={{ fontSize: 18, color: '#fff' }} />
                      </Box>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#fff' }}>
                        Getting Around
                      </Typography>
                    </Box>
                    <Box sx={{ p: 2.5 }}>
                      {gettingAroundTips.map((t, i) => {
                        const TIcon = t.icon;
                        return (
                          <Box key={i} sx={{
                            display: 'flex', alignItems: 'center', gap: 1.5, py: 1.2,
                            borderBottom: i < gettingAroundTips.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none',
                          }}>
                            <Box sx={{
                              width: 32, height: 32, borderRadius: '10px',
                              bgcolor: 'rgba(59,130,246,0.08)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              flexShrink: 0,
                            }}>
                              <TIcon sx={{ fontSize: 16, color: '#3b82f6' }} />
                            </Box>
                            <Typography sx={{ fontSize: '0.84rem', color: '#555', fontWeight: 500, lineHeight: 1.5 }}>
                              {t.text}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Grid>

                {/* Travel Tips */}
                <Grid item xs={12} md={6}>
                  <Box sx={{
                    bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden',
                    border: '1px solid rgba(0,0,0,0.06)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.04)', height: '100%',
                    transition: 'all 350ms ease',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 40px rgba(0,0,0,0.08)' },
                  }}>
                    <Box sx={{
                      background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                      px: 2.5, py: 1.8, display: 'flex', alignItems: 'center', gap: 1.5,
                      position: 'relative', overflow: 'hidden',
                    }}>
                      <Box sx={{ position: 'absolute', top: -10, right: -10, width: 50, height: 50, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                      <Box sx={{
                        width: 36, height: 36, borderRadius: '10px',
                        background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <TipsAndUpdatesIcon sx={{ fontSize: 18, color: '#fff' }} />
                      </Box>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#fff' }}>
                        Travel Tips
                      </Typography>
                    </Box>
                    <Box sx={{ p: 2.5 }}>
                      {travelTips.map((t, i) => {
                        const TIcon = t.icon;
                        return (
                          <Box key={i} sx={{
                            display: 'flex', alignItems: 'center', gap: 1.5, py: 1.2,
                            borderBottom: i < travelTips.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none',
                          }}>
                            <Box sx={{
                              width: 32, height: 32, borderRadius: '10px',
                              bgcolor: 'rgba(245,158,11,0.08)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              flexShrink: 0,
                            }}>
                              <TIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                            </Box>
                            <Typography sx={{ fontSize: '0.84rem', color: '#555', fontWeight: 500, lineHeight: 1.5 }}>
                              {t.text}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* ═══════════ CTA FOOTER ═══════════ */}
            <Box sx={{
              background: 'linear-gradient(145deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
              borderRadius: '24px', p: { xs: 3, sm: 3.5 }, position: 'relative', overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.05)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <Box sx={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: `radial-gradient(circle, ${GOLD}12 0%, transparent 70%)` }} />
              <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box sx={{
                    width: 42, height: 42, borderRadius: '14px',
                    background: `linear-gradient(135deg, ${GOLD}, #b8941f)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: `0 8px 24px ${GOLD}30`,
                  }}>
                    <ContactPhoneIcon sx={{ fontSize: 20, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.9rem', md: '1.05rem' }, color: '#fff', lineHeight: 1.2 }}>
                      Plan Your Visit
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', fontWeight: 500 }}>
                      We're here to help you plan
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{
                  fontSize: '0.88rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.8, mb: 2.5,
                  maxWidth: 500,
                }}>
                  Combine your spiritual pilgrimage with sightseeing for a complete North Kerala experience. The temple office can help arrange transport and guides.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  {[
                    { label: 'Start Exploring', to: '/nearby/beaches', icon: ExploreIcon, gradient: ['#22c55e', '#16a34a'] },
                    { label: 'Contact Temple', to: '/contact', icon: PhoneIcon, gradient: ['#3b82f6', '#2563eb'] },
                    { label: 'Book a Pooja', to: '/poojas', icon: WbSunnyIcon, gradient: [GOLD, '#b8941f'] },
                  ].map((link) => {
                    const LIcon = link.icon;
                    return (
                      <Box
                        key={link.label}
                        component={RouterLink}
                        to={link.to}
                        sx={{
                          display: 'inline-flex', alignItems: 'center', gap: 1,
                          px: 2.5, py: 1, borderRadius: '14px',
                          background: `linear-gradient(135deg, ${link.gradient[0]}, ${link.gradient[1]})`,
                          textDecoration: 'none', color: '#fff',
                          transition: 'all 300ms ease',
                          boxShadow: `0 4px 16px ${link.gradient[0]}30`,
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 8px 28px ${link.gradient[0]}40`,
                          },
                        }}
                      >
                        <LIcon sx={{ fontSize: 16 }} />
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700 }}>{link.label}</Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>

          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="nearby" activePath="/nearby" />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
